import { z } from "zod";

export const ProductFieldsSchema = z.object({
  id: z.number(),
  importId: z.number(),
  importCode: z.string(),
  importTyp: z.string(),
  name: z.string(),
  description: z.string(),
  code: z.string(),
  aPriceNetto: z.number(),
  aPriceBrutto: z.number(),
  aVatValue: z.number(),
  bPriceNetto: z.number(),
  bPriceBrutto: z.number(),
  bVatValue: z.number(),
  cPriceNetto: z.number(),
  cPriceBrutto: z.number(),
  cVatValue: z.number(),
  dPriceNetto: z.number(),
  dPriceBrutto: z.number(),
  dVatValue: z.number(),
  ePriceNetto: z.number(),
  ePriceBrutto: z.number(),
  eVatValue: z.number(),
  fPriceNetto: z.number(),
  fPriceBrutto: z.number(),
  fVatValue: z.number(),
  gPriceNetto: z.number(),
  gPriceBrutto: z.number(),
  gVatValue: z.number(),
  hPriceNetto: z.number(),
  hPriceBrutto: z.number(),
  hVatValue: z.number(),
  isComplete: z.boolean(),
  isService: z.boolean(),
  estimatedQuantity: z.null(),
  srpPriceType: z.string(),
  aCurrencyCode: z.string(),
  bCurrencyCode: z.string(),
  cCurrencyCode: z.string(),
  dCurrencyCode: z.string(),
  eCurrencyCode: z.string(),
  fCurrencyCode: z.string(),
  gCurrencyCode: z.string(),
  hCurrencyCode: z.string(),
  barCode: z.null(),
  vatValue: z.number(),
  isMainProduct: z.boolean(),
  modificationDate: z.string(),
  quantityPrecision: z.number(),
  ean: z.string(),
  externalMarker: z.null(),
  fullName: z.string(),
  defaultImage: z.object({
    id: z.number(),
    type: z.string(),
    subtype: z.null(),
    fileName: z.string(),
    extension: z.null(),
    dbName: z.string(),
    mimeType: z.string(),
    fileSize: z.number(),
    dateCreate: z.string(),
    md5: z.string(),
    fileToken: z.string(),
    uris: z.object({ show: z.string(), get: z.string() }),
    isAttachment: z.boolean(),
    isTmp: z.boolean(),
    accountUser: z.null(),
    user: z.object({
      id: z.number(),
      type: z.string(),
      subtype: z.null(),
      name: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      avatar: z.null(),
    }),
  }),
  measureUnit: z.object({
    measureUnitId: z.number(),
    name: z.string(),
    shortName: z.string(),
    importId: z.number(),
    importCode: z.string(),
    importTyp: z.string(),
    id: z.number(),
  }),
  productGroup: z.object({
    id: z.number(),
    name: z.string(),
    description: z.string(),
    importId: z.null(),
    importCode: z.null(),
    importTyp: z.null(),
  }),
  vat: z.object({
    vatId: z.number(),
    importId: z.number(),
    importCode: z.string(),
    importTyp: z.string(),
    vatName: z.string(),
    vatValue: z.number(),
    vatDescription: z.string(),
    id: z.number(),
  }),
  category: z.object({
    id: z.number(),
    name: z.string(),
    ordering: z.number(),
    importId: z.null(),
    importCode: z.null(),
    importTyp: z.null(),
    i18ns: z.array(z.unknown()),
  }),
  i18ns: z.array(z.unknown()),
  externalMarkers: z.array(z.unknown()),
  relations: z.object({
    fileToProducts: z.array(
      z.object({
        id: z.number(),
        type: z.string(),
        subtype: z.string(),
        fileName: z.string(),
        extension: z.null(),
        dbName: z.string(),
        mimeType: z.string(),
        fileSize: z.number(),
        dateCreate: z.string(),
        md5: z.string(),
        fileToken: z.string(),
        uris: z.object({ show: z.string(), get: z.string() }),
        isAttachment: z.boolean(),
        isTmp: z.boolean(),
        accountUser: z.null(),
        user: z.null(),
        position: z.null(),
      })
    ),
    odbcReports: z.array(z.unknown()),
    productCompletes: z.array(z.unknown()),
  }),
  attributes: z.array(z.unknown()),
  availabilities: z.object({
    all: z.object({
      total: z.number(),
      reserved: z.number(),
      available: z.number(),
    }),
  }),
  availability: z.object({
    total: z.number(),
    reserved: z.number(),
    available: z.number(),
  }),
  estimatedAvailabilities: z.null(),
  estimatedAvailability: z.any(),
  storehouse: z.string(),
  prices: z.array(
    z.union([
      z.object({
        currencyCode: z.string(),
        priceLevel: z.string(),
        priceNetto: z.number(),
        priceBrutto: z.number(),
        priceVat: z.number(),
        fullName: z.string(),
        shortName: z.string(),
        precision: z.number(),
      }),
      z.object({
        currencyCode: z.string(),
        priceLevel: z.string(),
        priceNetto: z.number(),
        priceBrutto: z.number(),
        priceVat: z.number(),
      }),
    ])
  ),
  categories: z.array(
    z.object({
      id: z.number(),
      type: z.string(),
      subtype: z.null(),
      name: z.string(),
      breadcrumbs: z.array(
        z.object({
          id: z.number(),
          type: z.string(),
          subtype: z.null(),
          name: z.string(),
          breadcrumbs: z.array(z.unknown()),
        })
      ),
    })
  ),
  similarProductIds: z.array(z.number()),
});

export const PartialProductFieldsSchema = ProductFieldsSchema.deepPartial();

export const ProductProductExtraFieldsSchema = z.object({
  attributes: z.array(z.any()), // TODO: define schema
  relations: z.object({
    tasks: z.array(z.any()),
    odbcReports: z.array(z.any()),
    commissions: z.array(z.any()),
  }),
});
export const PartialProductProductExtraFieldsSchema =
  ProductProductExtraFieldsSchema.deepPartial();

export const ProductSchema = z.intersection(
  ProductFieldsSchema,
  ProductProductExtraFieldsSchema
);

export type ProductFields = z.infer<typeof ProductSchema>;
export type PartialProductFields = z.infer<typeof PartialProductFieldsSchema>;
export type ProductExtraFields = z.infer<
  typeof ProductProductExtraFieldsSchema
>;
export type PartialProductExtraFields = z.infer<
  typeof PartialProductProductExtraFieldsSchema
>;
export type Product = z.infer<typeof ProductSchema>;
